<template>
  <div class="app-page">
    <Header ref="header" helpURL="studio/datasets/">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
      <template #page-slot>
        <BreadcrumbSelectProjectAndDataset
        />
      </template>
      <template #extra-slot>
        <TaskEditModeCard
          v-if="mode === 'edit' && route.name !== 'datasets.manager'"
          :annotationSetToEdit="annotationSetToEdit"
          :reviewSettings="reviewSettings"
          @update:annotation-set-to-edit="handleAnnotationSetToEditChanged"
          @stop-editing="handleStopEditing"
        />
      </template>
    </Header>
    <div class="main-page-div">
      <main
        id="drop-area"
        class="main-display-area"
      >
        <router-view />
      </main>
    </div>
    <TasksModal />
  </div>
</template>

<script setup>
import {
  computed, watch, onBeforeMount,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import Header from '@/components/Header.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import TaskEditModeCard from '@/components/DatasetComponent/AnnotationTool/TaskEditModeCard.vue';
import { useEditorStore } from '@/stores/useEditorStore.js';
import { useEditorTaskStore } from '@/stores/useEditorTaskStore.js';
import TasksModal from '@/components/TasksStatusComponent/TasksModalV2.vue';
import BreadcrumbSelectProjectAndDataset from '@/components/BreadcrumbSelectProjectAndDataset.vue';
import { decodeID, encodeID } from '@/assets/js/utils.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

const route = useRoute();
const router = useRouter();
const store = useStore();
const dataConnect = new DatastoreConnect();

let {
  mode, annotationSetToEdit,
} = storeToRefs(useEditorStore());
let { reviewSettings } = storeToRefs(useEditorTaskStore());

const currentProject = computed({
  get: () => store.state.projects.currentProject,
  set: (value) => store.commit('projects/setCurrentProject', value),
});

// Watch for project changes and update URL
watch(
  () => currentProject.value?.id,
  (newProjectId) => {
    if (newProjectId && route.params.projectId) {
      const currentProjectIdFromRoute = decodeID(route.params.projectId);
      
      // Only update URL if the project ID actually changed
      if (currentProjectIdFromRoute !== newProjectId) {
        console.log('Project changed from header, updating URL:', newProjectId);
        const projectUid = encodeID(newProjectId, 'p');
        router.push({
          name: 'datasets.manager',
          params: { projectId: projectUid },
          query: {} // Clear query parameters
        });
      }
    }
  }
);

// Clean up old query parameters that may be added by BreadcrumbSelectProjectAndDataset
watch(
  () => route.query,
  (newQuery) => {
    // Check if old format query parameters exist (project or dataset)
    if ((newQuery.project || newQuery.dataset) && route.name === 'datasets.manager') {
      console.log('Cleaning up old query parameters:', newQuery);
      router.replace({
        name: route.name,
        params: route.params,
        query: {} // Remove old query parameters
      });
    }
  },
  { deep: true }
);

// Load project from route params
onBeforeMount(async () => {
  console.log('Datasets.vue mounting, projectId param:', route.params.projectId);
  
  if (route.params.projectId) {
    const projectIdParam = route.params.projectId;
    let projectId = null;
    
    if (projectIdParam.startsWith('p-')) {
      // Decode hex format (p-xxxx) to decimal
      projectId = parseInt(projectIdParam.substring(2), 16);
      console.log('Decoded projectId from UID format:', projectIdParam, 'to', projectId);
    } else {
      // Try to parse as numeric
      const parsedId = parseInt(projectIdParam);
      projectId = isNaN(parsedId) ? null : parsedId;
      console.log('Parsed projectId as numeric:', projectIdParam, 'to', projectId);
    }
    
    if (projectId) {
      try {
        console.log('Loading project with ID:', projectId);
        const response = await dataConnect.getProject({ project_id: projectId });
        const project = response?.result || response;
        console.log('Project loaded:', project);
        if (project && !project.error) {
          currentProject.value = project;
          console.log('Project set in current project');
        } else {
          console.error('Project error:', project?.error);
        }
      } catch (error) {
        console.error('Failed to load project:', error);
      }
    } else {
      console.warn('Could not parse projectId:', projectIdParam);
    }
  } else {
    console.warn('No projectId in route params');
  }
});

watch(
  () => route.fullPath,
  (newPath) => {
    if (newPath.includes('/viewer')) {
      // Reinitialize stores
      const newEditorStore = useEditorStore();
      const newEditorTaskStore = useEditorTaskStore();

      // Re-import store refs
      ({
        mode, annotationSetToEdit,
      } = storeToRefs(newEditorStore));
      ({ reviewSettings } = storeToRefs(newEditorTaskStore));
    }
  },
  { immediate: true },
);

const hasDatasetNavigation = computed(() => !route.fullPath.includes('/manager') && !route.fullPath.includes('/viewer'));

function handleAnnotationSetToEditChanged(set) {
  annotationSetToEdit.value = set;
  if (reviewSettings.value) {
    reviewSettings.value.reviewTask.source_annotation_set_id = set.id;
    reviewSettings.value.reviewTask.dest_annotation_set_id = set.id;
  }
}

function handleStopEditing() {
  annotationSetToEdit.value = null;
  if (reviewSettings.value) {
    reviewSettings.value = null;
  }
  mode.value = 'view';
}
</script>

<style lang="scss" scoped>

</style>
